import { getPSTDate, toIsoDate } from "@common/helpers/date";
import { AccruedRecognitionPeriod } from "@common/helpers/domains/accounting/accruedBillingRule";
import {
    toAccruedRecognitionPeriods,
    unbilledBalanceAsOf,
} from "@common/helpers/domains/accounting/accruedUnbilledBalance";
import {
    ScheduleBillingEligibilityInput,
    canMarkScheduleBilled,
    canMarkScheduleUnbilled,
    isBillableAccruedSchedule,
} from "@common/helpers/domains/accounting/scheduleBillingEligibility";
import { ScheduleApplicationListItem } from "@common/schemas/domains/accounting/scheduleApplicationList.schema";
import {
    ExpenseSubMode,
    RevenueSubMode,
    ScheduleApplicationStatus,
    ScheduleBillingStatus,
    ScheduleSourceType,
    ScheduleType,
} from "@common/types/domains/accounting/schedule";
import { getScheduleRowApplicationId } from "./scheduleApplicationRowId";

/** What a schedule row needs for "Mark as billed" and "Mark as unbilled". */
export interface ScheduleBillingRowInfo {
    scheduleType: ScheduleType;
    revenueSubMode: RevenueSubMode | null;
    expenseSubMode: ExpenseSubMode | null;
    billedAmount: number;
    billingStatus: ScheduleBillingStatus | null;
    lastBilledDate: string | null;
    billingCount: number;
    /** The invoice or payment date, for "Billed by payment on …". */
    sourceDate: string | null;
    /**
     * The invoice number, for "Billed by invoice …". Kept apart from the
     * row's `description`, which line-scoped rows replace with the line text.
     */
    sourceLabel: string;
    recognitionAccountName: string | null;
    /** Posted recognition, which the unbilled balance is taken from. */
    recognitionPeriods: AccruedRecognitionPeriod[];
}

export interface ScheduleBillingTarget {
    applicationId: string;
    entityId: number;
    billing: ScheduleBillingRowInfo;
}

interface ScheduleBillingRow {
    id: string;
    /** Set on line-item slices, whose own id is composite. */
    scheduleApplicationId?: string;
    entityId: number;
    sourceType: ScheduleSourceType;
    status: ScheduleApplicationStatus;
    amount: number;
    billing: ScheduleBillingRowInfo;
}

export function buildScheduleBillingRowInfo(
    application: ScheduleApplicationListItem,
): ScheduleBillingRowInfo {
    return {
        scheduleType: application.scheduleType,
        revenueSubMode: application.revenueSubMode,
        expenseSubMode: application.expenseSubMode,
        billedAmount: application.billedAmount,
        billingStatus: application.billingStatus,
        lastBilledDate: application.lastBilledDate,
        billingCount: application.billingCount,
        sourceDate: application.source.date,
        sourceLabel: application.source.label,
        recognitionAccountName: application.recognitionAccount?.name ?? null,
        recognitionPeriods: toAccruedRecognitionPeriods(application),
    };
}

function toEligibility(
    row: ScheduleBillingRow,
): ScheduleBillingEligibilityInput {
    return {
        scheduleType: row.billing.scheduleType,
        revenueSubMode: row.billing.revenueSubMode,
        expenseSubMode: row.billing.expenseSubMode,
        sourceType: row.sourceType,
        status: row.status,
        amount: row.amount,
        billedAmount: row.billing.billedAmount,
    };
}

export function isRowBillableAccruedSchedule(row: ScheduleBillingRow): boolean {
    return isBillableAccruedSchedule(toEligibility(row));
}

export function canRowMarkBilled(row: ScheduleBillingRow): boolean {
    return canMarkScheduleBilled(toEligibility(row));
}

/** A billable schedule whose recognition hasn't reached today's month yet. */
export function hasRowNothingToBillToday(row: ScheduleBillingRow): boolean {
    return getUnbilledBalance(row.billing, toIsoDate(getPSTDate())) === 0;
}

export function canRowMarkUnbilled(row: ScheduleBillingRow): boolean {
    return canMarkScheduleUnbilled(toEligibility(row));
}

export function toScheduleBillingTarget(
    row: ScheduleBillingRow,
): ScheduleBillingTarget {
    return {
        applicationId: getScheduleRowApplicationId(row),
        entityId: row.entityId,
        billing: row.billing,
    };
}

/** Today, or the first open day when today is locked. */
export function getDefaultBilledDate(
    firstUnlockedDate: string | undefined,
): string {
    const today = toIsoDate(getPSTDate());
    return firstUnlockedDate && today < firstUnlockedDate
        ? firstUnlockedDate
        : today;
}

export function getUnbilledBalance(
    billing: ScheduleBillingRowInfo,
    asOfDate: string,
): number {
    return unbilledBalanceAsOf({
        periods: billing.recognitionPeriods,
        billedAmount: billing.billedAmount,
        asOfDate,
    });
}
